/**
 * MobileTimesheetTopMenu
 *
 * Mobile-optimized top navigation for TimesheetView.
 * Replaces the desktop topMenu div when isMobile === 2.
 * Uses MUI Box, IconButton, and Typography for a compact AppBar-style header.
 */
import React from "react";
import { Box, IconButton, Typography, Fade } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import Langs from "../../../app/lang/langs";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.28s cubic-bezier(0.4, 0, 0.2, 1)";

const iconButtonSx = {
  minWidth: 44,
  minHeight: 44,
  borderRadius: "12px",
  color: "rgba(0,0,0,0.7)",
  transition: TRANSITION_SMOOTH,
  "&:hover": {
    backgroundColor: "rgba(255,144,21,0.08)",
    color: "rgb(255,144,21)",
    boxShadow: "0 2px 8px rgba(255,144,21,0.15)",
    transform: "scale(1.06)",
  },
  "&:active": {
    transform: "scale(0.96)",
  },
};

/**
 * @param {Object} props
 * @param {Object|null} props.selectedTimesheet - the currently selected timesheet or null
 * @param {string}      props.teamLabel         - myTeam.label
 * @param {boolean}     props.hasModifications  - whether unsaved changes exist
 * @param {Function}    props.onBackClick       - handles nav back (with save check)
 * @param {Function}    props.onExportClick     - handles CRA PDF export
 */
export default function MobileTimesheetTopMenu({
  selectedTimesheet,
  teamLabel,
  hasModifications,
  onBackClick,
  onExportClick,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        bgcolor: "rgba(255,255,255,0.78)",
        backdropFilter: "blur(20px) saturate(1.4)",
        WebkitBackdropFilter: "blur(20px) saturate(1.4)",
        borderBottom: "1px solid rgba(0,40,128,0.06)",
        boxShadow:
          "0 4px 6px -4px rgba(0,40,128,0.05), 0 2px 3px -2px rgba(0,0,0,0.03)",
        px: 1.5,
        py: 0.75,
        transition: TRANSITION_SMOOTH,
        position: "relative",
        zIndex: 10,
        "&::after": {
          content: '""',
          position: "absolute",
          bottom: 0,
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(255,144,21,0.15), transparent)",
        },
      }}
    >
      {/* Main row: back button / title / actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 48,
          px: 0.5,
        }}
      >
        {/* Left: back arrow (when a timesheet is selected) + title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, minWidth: 0 }}>
          {selectedTimesheet && (
            <Fade in timeout={200}>
              <IconButton
                onClick={onBackClick}
                size="large"
                aria-label="Back to timesheets"
                sx={iconButtonSx}
              >
                <ArrowBackIcon />
              </IconButton>
            </Fade>
          )}

          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.15rem",
                color: "rgb(0,0,0)",
                letterSpacing: "-0.025em",
                lineHeight: 1.2,
                transition: "color 0.2s ease",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              <Langs str="Timesheet" />
            </Typography>

            {/* Subtitle: team label or month name */}
            <Typography
              sx={{
                fontSize: "0.78rem",
                color: "rgba(0,0,0,0.5)",
                fontWeight: 500,
                lineHeight: 1.3,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selectedTimesheet
                ? selectedTimesheet.month
                : teamLabel || ""}
            </Typography>
          </Box>
        </Box>

        {/* Right: export button */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {selectedTimesheet && (
            <Fade in timeout={250}>
              <IconButton
                onClick={onExportClick}
                size="large"
                aria-label="Export CRA PDF"
                sx={iconButtonSx}
              >
                <FileDownloadOutlinedIcon />
              </IconButton>
            </Fade>
          )}
        </Box>
      </Box>
    </Box>
  );
}
